import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import { CandidateAssessmentSummaryBadge } from '@/modules/candidates/components/CandidateLedgerAssessment';
import type { CandidateJobApi } from '@/modules/candidates/types';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/modules/ui/components/alert-dialog/AlertDialog';
import { Button } from '@/modules/ui/components/button/Button';
import { Separator } from '@/modules/ui/components/separator/Separator';

import {
  AnnotationPicker,
  type AnnotationReason,
  buildAnnotation,
} from './AnnotationPicker';
import CandidateScore from './CandidateScore';
import { RejectionMessagePreview } from './rejection-message/RejectionMessagePreview';

export interface CandidateForAnnotation {
  id: string;
  candidateJobId: string;
  fullName: string;
  headline: string | null;
  location: string | null;
  score: number | null | undefined;
  assessment: CandidateJobApi['assessment'];
  assessmentLedgerRevisionId: string | null;
  strengths: Array<{ summary: string }>;
  clarifications: Array<{ summary: string }>;
}

interface BulkCandidateAnnotationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  isPositive: boolean;
  candidates: CandidateForAnnotation[];
  onConfirm: (annotations: Record<string, string | null>) => void;
  isLoading?: boolean;
}

interface AnnotationEntry {
  selectedReasons: AnnotationReason[];
  otherText: string;
}

const emptyEntry = (): AnnotationEntry => ({
  selectedReasons: [],
  otherText: '',
});

const buildEntries = (
  candidates: CandidateForAnnotation[],
): Record<string, AnnotationEntry> =>
  Object.fromEntries(candidates.map((c) => [c.id, emptyEntry()]));

export function BulkCandidateAnnotationDialog({
  open,
  onOpenChange,
  jobId,
  isPositive,
  candidates,
  onConfirm,
  isLoading,
}: BulkCandidateAnnotationDialogProps) {
  const [entries, setEntries] = useState<Record<string, AnnotationEntry>>(() =>
    buildEntries(candidates),
  );
  const [selectedId, setSelectedId] = useState<string>(candidates[0]?.id ?? '');

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setEntries(buildEntries(candidates));
      setSelectedId(candidates[0]?.id ?? '');
    }
  };

  // selectedId may be stale (initialised when candidates was empty) — fall back to first
  const effectiveId = candidates.find((c) => c.id === selectedId)
    ? selectedId
    : (candidates[0]?.id ?? '');

  const selected = candidates.find((c) => c.id === effectiveId);
  const entry = entries[effectiveId] ?? emptyEntry();

  const updateEntry = (patch: Partial<AnnotationEntry>) => {
    setEntries((prev) => ({
      ...prev,
      [effectiveId]: { ...(prev[effectiveId] ?? emptyEntry()), ...patch },
    }));
  };

  const annotatedCount = Object.values(entries).filter(
    (e) => e.selectedReasons.length > 0,
  ).length;

  const currentIndex = candidates.findIndex((c) => c.id === effectiveId);
  const isLast = currentIndex === candidates.length - 1;

  const handleBack = () => {
    const prev = candidates[currentIndex - 1];
    if (prev) setSelectedId(prev.id);
  };

  const handleNext = () => {
    const next = candidates[currentIndex + 1];
    if (next) setSelectedId(next.id);
  };

  const handleConfirm = () => {
    const annotations: Record<string, string | null> = {};
    for (const candidate of candidates) {
      const e = entries[candidate.id];
      annotations[candidate.id] = e
        ? buildAnnotation(e.selectedReasons, e.otherText)
        : null;
    }
    onConfirm(annotations);
  };

  if (!selected) return null;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="gap-0 p-0 sm:max-w-3xl">
        <button
          type="button"
          onClick={() => handleOpenChange(false)}
          disabled={isLoading}
          className="ring-offset-background absolute top-4 right-4 z-10 cursor-pointer rounded-xs opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none"
        >
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </button>
        <div className="flex min-h-0 overflow-hidden rounded-t-xl">
          {/* Left: candidate list */}
          <div className="flex w-56 shrink-0 flex-col border-r">
            <div className="border-b px-4 py-3">
              <p className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
                {candidates.length} candidates
              </p>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {candidates.map((candidate) => {
                const e = entries[candidate.id] ?? emptyEntry();
                const hasAnnotation = e.selectedReasons.length > 0;
                const isActive = candidate.id === effectiveId;
                return (
                  <button
                    key={candidate.id}
                    type="button"
                    onClick={() => setSelectedId(candidate.id)}
                    className={cn(
                      'w-full border-b px-4 py-3 text-left transition-colors last:border-b-0',
                      isActive ? 'bg-neutral-50' : 'hover:bg-neutral-50/60',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className={cn(
                          'mt-1 size-2 shrink-0 rounded-full transition-colors',
                          hasAnnotation
                            ? isPositive
                              ? 'bg-semantic-560'
                              : 'bg-semantic-550'
                            : 'border border-neutral-300',
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            'truncate text-sm leading-tight font-medium',
                            isActive ? 'text-neutral-900' : 'text-neutral-700',
                          )}
                        >
                          {candidate.fullName}
                        </p>
                        <div className="mt-1 flex items-center gap-1.5">
                          <CandidateScore score={candidate.score} size={11} />
                          {candidate.headline && (
                            <p className="text-muted-foreground max-w-[100px] truncate text-xs">
                              {candidate.headline}
                            </p>
                          )}
                        </div>
                        <div className="mt-1.5">
                          <CandidateAssessmentSummaryBadge
                            assessment={candidate.assessment}
                          />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-auto border-t px-4 py-2">
              <p className="text-muted-foreground text-[11px]">
                {annotatedCount} of {candidates.length} annotated
              </p>
            </div>
          </div>

          {/* Right: profile preview + annotation */}
          <div className="flex min-w-0 flex-1 flex-col">
            <AlertDialogHeader className="border-b px-6 py-4 pr-12">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <AlertDialogTitle className="truncate">
                    {isPositive
                      ? `Why are you submitting ${selected.fullName}?`
                      : `Why are you rejecting ${selected.fullName}?`}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="mt-0.5">
                    {isPositive
                      ? 'Your reason teaches the agent to find more like them.'
                      : 'Your reason teaches the agent to filter out similar profiles.'}
                  </AlertDialogDescription>
                </div>
                <CandidateScore score={selected.score} size={14} />
              </div>
              <CandidateAssessmentSummaryBadge
                assessment={selected.assessment}
              />
            </AlertDialogHeader>

            <div className="max-h-[360px] overflow-y-auto px-6 py-4">
              {(selected.headline ||
                selected.location ||
                selected.strengths.length > 0 ||
                selected.clarifications.length > 0) && (
                <>
                  <div className="mb-4 space-y-1.5">
                    {selected.headline && (
                      <p className="text-sm font-semibold text-neutral-800">
                        {selected.headline}
                      </p>
                    )}
                    {selected.location && (
                      <div className="text-muted-foreground flex items-center gap-1 text-xs">
                        <MapPin className="size-3 shrink-0" />
                        {selected.location}
                      </div>
                    )}
                    {(selected.strengths.length > 0 ||
                      selected.clarifications.length > 0) && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {selected.strengths.map((s, i) => (
                          <ProfileBadge
                            key={`s-${i}`}
                            label={s.summary}
                            strong
                          />
                        ))}
                        {selected.clarifications.map((c, i) => (
                          <ProfileBadge
                            key={`c-${i}`}
                            label={c.summary}
                            strong={false}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <Separator className="mb-4" />
                </>
              )}

              <AnnotationPicker
                isPositive={isPositive}
                selectedReasons={entry.selectedReasons}
                onReasonsChange={(reasons) =>
                  updateEntry({ selectedReasons: reasons })
                }
                otherText={entry.otherText}
                onOtherTextChange={(text) => updateEntry({ otherText: text })}
              />
            </div>

            <div className="flex justify-between border-t px-6 py-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                disabled={isLoading}
                className={cn('text-xs', currentIndex === 0 && 'invisible')}
              >
                <ChevronLeft className="mr-1 size-3" />
                Back
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNext}
                disabled={isLoading}
                className={cn('text-xs', isLast && 'invisible')}
              >
                Next
                <ChevronRight className="ml-1 size-3" />
              </Button>
            </div>
          </div>
        </div>

        {!isPositive && (
          <div className="border-t px-6 py-4">
            <RejectionMessagePreview jobId={jobId} candidates={candidates} />
          </div>
        )}

        <AlertDialogFooter className="border-t px-6 py-4 sm:justify-between">
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            loading={isLoading}
            className={cn(
              isPositive
                ? 'bg-semantic-560 hover:bg-semantic-560/90'
                : 'bg-semantic-550 hover:bg-semantic-550/90',
            )}
          >
            {isPositive ? (
              <ThumbsUp className="mr-2 size-4" />
            ) : (
              <ThumbsDown className="mr-2 size-4" />
            )}
            {isPositive
              ? `Submit all (${candidates.length})`
              : `Reject all (${candidates.length})`}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ProfileBadge({ label, strong }: { label: string; strong: boolean }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs',
        strong
          ? 'bg-primary/10 text-primary font-medium'
          : 'bg-neutral-100 text-neutral-600',
      )}
    >
      {label}
    </span>
  );
}
